import { ChangeEvent, useMemo, useState } from 'react';

import Checkbox from '@components/checkbox/Checkbox';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import CustomTable from '@components/custom-table/CustomTable';
import {
  CustomTableHeaderProps,
  CustomTableRowData
} from '@components/custom-table/types';
import { Icon, Tooltip } from '@components/index';
import {
  DEFAULT_PLACEHOLDER,
  NULLDATE
  // PAGINATION_LIMIT
} from '@constants/generic';
import { TermsConfig } from '@types';

import { EligibleMember } from '../../types';

type MemberTableProps = {
  members: EligibleMember[];
  selectedIds: Set<number>;
  onToggle: (groupMemberId: number) => void;
  onTogglePageAll: (addIds: number[], removeIds: number[]) => void;
  description: string;
  periodLabel?: string;
  showConsent?: boolean;
};

const formatDisplayDate = (dateStr: string): string => {
  if (!dateStr || dateStr === NULLDATE) return DEFAULT_PLACEHOLDER;
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
};

const HEADERS: CustomTableHeaderProps[] = [
  { Header: 'Name', accessor: 'name', width: '40%' },
  { Header: 'Current amount', accessor: 'amount', width: '20%' },
  { Header: 'Next request date', accessor: 'nextDueAt', width: '30%' },
  { Header: '', accessor: 'isPaymentLinkSent', width: '10%' }
];

const MemberTermSplitTooltip = ({
  termDetails,
  totalAmount,
  tooltipId
}: {
  termDetails: TermsConfig[];
  totalAmount: string;
  tooltipId: string;
}) => (
  <Tooltip
    id={tooltipId}
    type="custom"
    variant="light"
    className="!z-40 !border !border-cofeeNeutral4 !bg-white !p-0 !shadow-lg"
    place="top"
  >
    <div className="flex min-w-44 flex-col gap-1.5 p-3">
      {termDetails.map((term) => (
        <div
          key={term.term_number}
          className="flex items-center justify-between gap-4"
        >
          <span className="text-xs text-cofeeNeutral7">
            {term.label || term.term_label || `Term ${term.term_number}`}
          </span>
          <CurrencyAmount
            value={term.fee_amount}
            className="text-xs font-medium text-cofeeNeutral10"
          />
        </div>
      ))}
      <div className="mt-1 border-t border-cofeeNeutral4 pt-1.5">
        <div className="flex items-center justify-between gap-4">
          <span className="text-xs font-medium text-cofeeNeutral10">
            Total amount
          </span>
          <CurrencyAmount
            value={totalAmount}
            className="text-xs font-medium text-cofeeNeutral10"
          />
        </div>
      </div>
    </div>
  </Tooltip>
);

const MemberAmountCell = ({ member }: { member: EligibleMember }) => {
  const tooltipId = `member-term-split-${member.group_member_id}`;
  const hasTermDetails = member.term_details && member.term_details.length > 0;

  if (!hasTermDetails) {
    return <CurrencyAmount value={member.amount} className="text-sm" />;
  }

  return (
    <>
      <span
        data-tooltip-id={tooltipId}
        className="inline-flex cursor-default border-b border-dotted border-cofeeNeutral10"
      >
        <CurrencyAmount value={member.amount} className="text-sm" />
      </span>
      <MemberTermSplitTooltip
        termDetails={member.term_details!}
        totalAmount={member.amount}
        tooltipId={tooltipId}
      />
    </>
  );
};

const PAGINATION_LIMIT = 5;

const MemberTable = ({
  members,
  selectedIds,
  onToggle,
  onTogglePageAll,
  description,
  periodLabel = 'month',
  showConsent = true
}: MemberTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const hasPagination = members.length > PAGINATION_LIMIT;

  const pageMembers = useMemo(() => {
    if (!hasPagination) return members;
    const start = (currentPage - 1) * PAGINATION_LIMIT;
    return members.slice(start, start + PAGINATION_LIMIT);
  }, [members, currentPage, hasPagination]);

  const data: CustomTableRowData<number>[] = pageMembers.map((member) => ({
    id: member.group_member_id,
    name: (
      <span className="text-sm text-cofeeNeutral10">
        {member.name}
        {member.status !== 'active' && (
          <span className="ml-1 italic text-cofeeNeutral7">
            ({member.status === 'inactive' ? 'Inactive' : member.status})
          </span>
        )}
      </span>
    ),
    amount: <MemberAmountCell member={member} />,
    nextDueAt: (
      <span className="text-sm text-cofeeNeutral10">
        {formatDisplayDate(member.next_due_at)}
      </span>
    ),
    isPaymentLinkSent:
      selectedIds.has(member.group_member_id) &&
      member.is_payment_link_sent &&
      showConsent ? (
        <>
          <Icon
            name="info-orange"
            size="medium"
            data-tooltip-id={`payment-link-sent-${member.group_member_id}`}
          />
          <Tooltip
            id={`payment-link-sent-${member.group_member_id}`}
            type="text"
            tooltipText={`Payment link for this ${periodLabel} already sent`.replace(
              /\s{2,}/g,
              ' '
            )}
            // className="!bg-white !p-4 !text-cofeeGreyBlue9"
            place="bottom-end"
            variant="light"
            className="!p-4 !shadow-lg"
          />
        </>
      ) : null
  }));

  const selectedRows = members
    .filter((m) => selectedIds.has(m.group_member_id))
    .map((m) => m.group_member_id);

  const allMemberIds = useMemo(
    () => members.map((m) => m.group_member_id),
    [members]
  );
  const isAllMembersSelected =
    allMemberIds.length > 0 && allMemberIds.every((id) => selectedIds.has(id));

  const handleSelectAllMembers = (checked: boolean) => {
    if (checked) onTogglePageAll(allMemberIds, []);
    else onTogglePageAll([], allMemberIds);
  };

  return (
    <div className="pb-4">
      <div className="mb-3 flex items-center justify-between gap-4">
        <p className="text-sm text-cofeeNeutral7">{description}</p>
        {hasPagination && (
          <Checkbox
            label={`Select all (${members.length})`}
            isChecked={isAllMembersSelected}
            handleChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleSelectAllMembers(e.target.checked)
            }
            labelStyle="!text-cofeeNeutral10 !font-medium !text-sm whitespace-nowrap"
            className="h-11 shrink-0 rounded-lg border border-grey40 bg-theme px-4 py-2.5 disabled:border-grey30 disabled:bg-grey10"
          />
        )}
      </div>
      <div className="overflow-y-auto">
        <CustomTable<number>
          headers={HEADERS}
          data={data}
          hasMultiSelect
          selectedRows={selectedRows}
          handleCheckboxSelect={(row) => onToggle(row.id)}
          handleSelectAll={onTogglePageAll}
          className="!pb-0"
          disableSelectedHighlight
          {...(hasPagination && {
            totalCount: members.length,
            currentPage,
            onPageChange: setCurrentPage,
            paginationLimit: PAGINATION_LIMIT
          })}
        />
      </div>
    </div>
  );
};

export default MemberTable;
